<!--
	The header lives inside the body slot on purpose: the sheet's body is the flexing,
	scrollable region, so content scrolls from just under the drag handle down to the
	action buttons, which stay pinned in the footer.
-->
<app-bottom-sheet #sheet ariaLabel="جزئیات سفارش" ariaLabelledBy="order-detail-title">
	<ng-template #body>
		@if (order(); as order) {
			<div class="flex w-full items-center justify-between gap-1 pb-3 text-right">
				<div class="flex min-w-0 flex-col gap-1">
					<h2
						id="order-detail-title"
						class="flex min-w-0 items-center gap-1 text-base font-bold text-gray-900">
						<ui-icon
							class="shrink-0 text-gray-800"
							width="20"
							height="20"
							data-cy="order-detail-symbol-icon"
							[iconName]="order.symbolIcon" />
						<span class="truncate" data-cy="order-detail-symbol">{{ order.symbolShortName }}</span>
					</h2>
					<span class="truncate text-xs text-gray-500" data-cy="order-detail-symbol-short-name">{{
						order.symbolName
					}}</span>
				</div>
				@if (lastPrice(); as lastPrice) {
					<div
						class="flex shrink-0 flex-col items-end gap-1 text-sm font-medium"
						data-cy="order-detail-last-price">
						<ui-number
							format="1.0-0"
							class="text-gray-900"
							data-cy="order-detail-last-price-value"
							[value]="lastPrice.price" />
						<span
							class="flex items-center gap-1"
							data-cy="order-detail-last-price-change"
							[class.text-green-500]="lastPriceUp()"
							[class.text-red-500]="!lastPriceUp()">
							<span>(%<ui-number dir="ltr" [value]="lastPrice.changePercent" [maxDigit]="2" />)</span>
							<ui-number format="1.0-0" dir="ltr" [value]="lastPrice.change" />
						</span>
					</div>
				}
			</div>

			<div
				class="flex flex-col divide-y pb-3 divide-stroke-100 text-3.25 font-medium text-gray-900"
				data-cy="order-detail-info">
				<div class="flex h-9 w-full items-center justify-between gap-2">
					<span>نوع سفارش</span>
					<span
						[class.text-green-500]="order.side === OrderSide.Buy"
						[class.text-red-500]="order.side === OrderSide.Sell">
						{{ order.side === OrderSide.Buy ? 'خرید' : 'فروش' }}
					</span>
				</div>
				<div class="flex h-9 w-full items-center justify-between gap-2">
					<span>وضعیت</span>
					<span
						data-cy="order-detail-status"
						[class.text-blue-500]="!isErrorState()"
						[class.text-red-500]="isErrorState()"
						>{{ statusLabel() }}</span
					>
				</div>
				<div class="flex h-9 w-full items-center justify-between gap-2">
					<span>تعداد</span>
					<ui-number data-cy="order-detail-quantity" [value]="quantity()" />
				</div>
				<div class="flex h-9 w-full items-center justify-between gap-2">
					<span>انجام‌شده</span>
					<ui-number data-cy="order-detail-executed-quantity" [value]="order.executedVolume" />
				</div>
				<div class="flex h-9 w-full items-center justify-between gap-2">
					<span>قیمت</span>
					<ui-number format="1.0-0" data-cy="order-detail-price" [value]="price()" />
				</div>
				<div class="flex h-9 w-full items-center justify-between gap-2">
					<span>زمان ثبت</span>
					<span data-cy="order-detail-created-at">{{ order.entryDate | jdate: 'time' | eDigit }}</span>
				</div>
			</div>

			@if (tradeRows().length) {
				<section class="mb-4 flex flex-col gap-2" data-cy="order-detail-trades">
					<h3 class="text-3.25 font-medium text-gray-800">ریز معاملات</h3>
					<ul class="flex flex-col gap-1">
						@for (trade of tradeRows(); track trade.id) {
							<li
								class="flex flex-col gap-1.5 rounded-lg border border-stroke-100 p-2"
								data-cy="order-detail-trade-item">
								<div class="flex items-center justify-between gap-2">
									<ui-number
										format="1.0-0"
										class="text-xs font-normal text-blue-500"
										data-cy="order-detail-trade-item-price"
										[value]="trade.price" />
									<span class="flex items-center gap-1 text-3.25 text-gray-800 text-xs font-normal">
										<span data-cy="order-detail-trade-percent">
											(%<ui-number dir="ltr" [value]="trade.percent" />)
										</span>
										<ui-number data-cy="order-detail-trade-quantity" [value]="trade.quantity" />
									</span>
								</div>

								<!--
									Each part occupies its own slice of a shared 0-100% track,
									offset by everything filled before it, so the parts tile the
									order's whole progress rather than each starting from zero.
									The slice is hidden from assistive tech because the same share
									is already stated as text next to the quantity.
								-->
								<div
									dir="ltr"
									class="w-full bg-disable-050"
									aria-hidden="true"
									data-cy="order-detail-trade-progress">
									<div
										data-cy="order-detail-trade-progress-segment"
										[style.margin-inline-start.%]="trade.offsetPercent"
										[style.width.%]="trade.percent">
										<ui-progress-bar variant="blue" size="small" [value]="100" />
									</div>
								</div>

								<div class="flex items-center justify-between gap-2 text-xs font-normal text-gray-600">
									<span class="flex items-center gap-1" data-cy="order-detail-trade-value">
										<span>ارزش معامله:</span>
										<ui-number format="1.0-0" [value]="trade.value" />
									</span>
									<span class="text-gray-800" data-cy="order-detail-trade-time">{{
										trade.executedAt | jdate: 'time' | eDigit
									}}</span>
								</div>
							</li>
						}
					</ul>
				</section>
			}
		}
	</ng-template>

	<ng-template #footer>
		@if (order() && showActions()) {
			<div class="flex w-full gap-2 pb-4" data-cy="order-detail-actions">
				<ui-button
					class="min-w-0 flex-1"
					variant="blue"
					model="outline"
					size="lg"
					extraButtonClasses="w-full"
					data-cy="order-detail-copy-action"
					label="کپی"
					(click)="onCopyClick()">
					<ui-icon leftIcon [iconName]="'copy'" />
				</ui-button>

				@if (canEditOrCancel()) {
					<ui-button
						class="min-w-0 flex-1"
						variant="blue"
						model="outline"
						size="lg"
						extraButtonClasses="w-full"
						data-cy="order-detail-edit-action"
						label="ویرایش"
						(click)="onEditClick()">
						<ui-icon leftIcon [iconName]="'edit'" />
					</ui-button>
					<ui-button
						class="min-w-0 flex-1"
						variant="red"
						model="outline"
						size="lg"
						extraButtonClasses="w-full"
						data-cy="order-detail-cancel-action"
						label="حذف"
						(click)="onCancelClick()">
						<ui-icon leftIcon [iconName]="'delete'" />
					</ui-button>
				}
			</div>
		}
	</ng-template>
</app-bottom-sheet>
